<!-- eslint-disable vue/multi-word-component-names -->

<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 24px">
        <!-- User Profile -->
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>
    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <!-- User Profile in Sidebar -->
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div>
                  <div>
                  <div class="font-weight-bold text-body-2 lowercase">
                    {{ userDetails?.name || 'Carregando...' }}
                  </div>
              </div>
              </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      
        <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="item.active"
          rounded="xl"
          class="mb-2 menu-item"
          :class="item.active ? 'active-item' : ''"
          lines="one"
          density="comfortable"
          @click="router.push(item.route)"
        >
          <template #prepend>
            <v-icon size="22" class="mr-2">{{ item.icon }}</v-icon>
          </template>

          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>

          <template #append v-if="item.badge">
            <v-chip size="small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>


    <!-- Main Content -->
    <v-main class="main-content">
      <div class="iframe-wrapper">
        <WhatsApp class="iframe-centered" :embedded="true" />
      </div>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeMount, watch } from "vue"; // Importar do Vue, não de constants
import { menuItems } from '@/constants/MenuItems' // Apenas o menuItems do arquivo de constantes
import { useRouter } from 'vue-router';
import axios from "axios";
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions';
import WhatsApp from '@/components/Whatsapp.vue'

const API_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');

const drawer = ref(true);
const authStore = useAuthStore();
const router = useRouter();      
const auth = useAuthStore();
const { 
  userDetails, 
  checkPermissionAndRedirect
} = usePermissions();
const token = computed(() => auth?.token);
const isMounted = ref(false);

const checkAccessAndInitialize = async () => {
  const hasAccess = await checkPermissionAndRedirect('config-wpp');
  if (!hasAccess) {
    return false; // Redireciona para /access-denied
  }
  
  // Se tem permissão, carrega o conteúdo
  isMounted.value = true;
  await fetchUserDetails();
  return true;
};

const colors = {
  primaryDark: "#123A5D",
  primaryLight: "#57C2E3",
  primaryGreen: "#A3C940",
  lightBg: "#E7F2F9",
};

const fetchUserDetails = async () => {
  if (!isMounted.value || !token.value) {
    console.log("fetchUserDetails: não executando - mounted:", isMounted.value, "token:", !!token.value);
    return;
  }

  try {
    console.log("fetchUserDetails: fazendo requisição");
    const response = await axios.get(`${API_BASE}/me`, { 
      headers: { Authorization: `Bearer ${token.value}` },
      timeout: 5000
    });

    if (isMounted.value && response.data) {
      userDetails.value = response.data;
      console.log("fetchUserDetails: sucesso:", response.data.name);
    }
  } catch (error) {
    console.error("fetchUserDetails: erro:", error?.response?.status, error?.message);
    if (isMounted.value) {
      userDetails.value = null;
    }
  }
}

const logout = () => {
  if (authStore && typeof authStore.logout === 'function') {
    authStore.logout();
  }
  if (router && typeof router.push === 'function') {
    router.push('/');
  }
};

// Watch para reagir a mudanças no token
watch(token, async (newToken, oldToken) => {
  if (newToken && newToken !== oldToken && isMounted.value) {
    await fetchUserDetails();
  }
}, { immediate: false });

// Lifecycle
onMounted(async () => {
  isMounted.value = true;
  console.log("Layout montado - auth:", auth.isAuthenticated, "token:", !!token.value);
  
  if (token.value) {
    await fetchUserDetails();
    await checkAccessAndInitialize();
  }
});

onBeforeMount(() => {
  isMounted.value = false;
});
</script>
<style scoped>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Component Styles */
.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pulse {
  animation: pulse 2s infinite;
}

.user-profile {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 8px 12px;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.menu-item .v-list-item__content {
  display: flex;
  align-items: center;
}

.menu-item .item-title {
  white-space: nowrap;    /* não quebra a linha */
  text-align: left;       /* evita centralização herdada */
}

/* Se o painel esquerdo tiver text-align:center, force à esquerda */
.left-panel, .left-panel * {
  text-align: left !important;
}

.menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0px 0;
  position: relative;
  overflow: hidden;
  padding-left: px;
  margin-top: 4px;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.notification-btn {
  transition: all 0.3s ease;
}

.notification-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.custom-drawer {
  background: linear-gradient(180deg, #123A5D 0%, #0f2a42 100%);
}

.sidebar-profile {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  margin: 8px;
  transition: all 0.3s ease;
}

.menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 4px 0;
  position: relative;
  overflow: hidden;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  transform: translateX(4px);
}

.active-item {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1) !important;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.active-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: #A3C940;
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
}

.main-content {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%);
}

.location-btn {
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
  transition: all 0.3s ease;
}

.location-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(87, 194, 227, 0.4);
}

.status-chip {
  animation: slideIn 0.5s ease-out;
}

.action-btn {
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
}

.stat-card {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #ffffff 0%, #f8fbfd 100%);
  border: 1px solid rgba(87, 194, 227, 0.1);
}

.stat-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

.icon-avatar {
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-card:hover .icon-avatar {
  transform: scale(1.1) rotate(5deg);
}

.stat-icon {
  transition: all 0.3s ease;
}

.stat-value {
  background: linear-gradient(45deg, #123A5D, #57C2E3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.drive-card {
  background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
  border: 1px solid rgba(87, 194, 227, 0.1);
}

.folders-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(87, 194, 227, 0.3) transparent;
}

.folders-container::-webkit-scrollbar {
  width: 6px;
}

.folders-container::-webkit-scrollbar-track {
  background: transparent;
}

.folders-container::-webkit-scrollbar-thumb {
  background: rgba(87, 194, 227, 0.3);
  border-radius: 3px;
}

.folder-item {
  animation: slideIn 0.5s ease-out forwards;
  opacity: 0;
  transition: all 0.3s ease;
  background: rgba(87, 194, 227, 0.05);
  border: 1px solid rgba(87, 194, 227, 0.1);
}

.folder-item:hover {
  background: rgba(87, 194, 227, 0.1);
  transform: translateX(4px);
  border-color: rgba(87, 194, 227, 0.2);
}

.folder-avatar {
  transition: all 0.3s ease;
}

.folder-item:hover .folder-avatar {
  transform: scale(1.1);
}

.drive-btn {
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
  transition: all 0.3s ease;
}

.drive-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(87, 194, 227, 0.4);
}

.quick-action-btn {
  transition: all 0.3s ease;
}

.quick-action-btn:hover {
  transform: translateY(-2px);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .stat-card {
    margin-bottom: 16px;
  }
  
  .user-profile span:last-child {
    display: none;
  }
}

@media (max-width: 600px) {
  .gradient-text {
    font-size: 1rem;
  }
  
  .quick-action-btn {
    width: 100%;
    margin-bottom: 8px;
  }
}
/* ... outros estilos ... */

/* Estilo para centralizar o iframe */
.main-content {
  background-color: #f5f7fa;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  position: relative;
}

.iframe-wrapper {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  overflow: hidden;
}

.iframe-centered {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  border: none;
}

</style>
